import { Box } from '@act/storybook';
import { Dialog, DialogContent, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { surveysRequests } from 'redux/requests/surveys.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { TrackingProvider } from '../tracking';
import AssignedSurveyHistoryTimeline from './components/AssignedSurveyHistoryTimeline';
import CloseableDialogTitle from './components/dialog-components/CloseableDialogTitle';

export interface AssignedSurveyHistoryDialogUniqueProps {
  assignedSurveyId: number;
}

export type AssignedSurveyHistoryDialogProps = GenericDialogProps<null> &
  AssignedSurveyHistoryDialogUniqueProps;

/**
 * Read-only audit view for one assigned survey, opened from the assigned-survey
 * dialog's overflow menu.
 *
 * Fetches on open and keeps the payload in local hook state: the history
 * response carries no survey definition, so writing it into the surveys slice
 * would clobber live assigned-survey state.
 *
 * @param assignedSurveyId - Assignment whose history to load
 * @param open - Whether the dialog is open
 * @param setOpen - Setter used to close the dialog
 * @returns History dialog
 */
const AssignedSurveyHistoryDialog = ({
  assignedSurveyId,
  open,
  setOpen,
}: AssignedSurveyHistoryDialogProps) => {
  const [history, isLoadingHistory, getAssignedSurveyHistory, historyError] =
    useApiCall(surveysRequests.getAssignedSurveyHistory);

  useEffect(() => {
    if (open) {
      getAssignedSurveyHistory({ params: { assignedSurveyId } });
    }
  }, [open, assignedSurveyId, getAssignedSurveyHistory]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <TrackingProvider contextName="assigned-survey-history-dialog">
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        scroll="body"
        aria-labelledby="assigned-survey-history-dialog"
      >
        <CloseableDialogTitle title="Survey history" handleClose={handleClose} />
        <DialogContent style={{ padding: '12px 24px 24px' }}>
          <Box display="flex" flexDirection="column" gap="12px" width="600px">
            {isLoadingHistory && (
              <>
                <Skeleton animation="wave" />
                <Skeleton animation="wave" />
                <Skeleton animation="wave" />
              </>
            )}
            {!isLoadingHistory && historyError && (
              <Typography component="div" variant="bodyMedium">
                This survey&apos;s history could not be loaded. A survey
                assigned moments ago may not be available yet.
              </Typography>
            )}
            {!isLoadingHistory && history && (
              <AssignedSurveyHistoryTimeline history={history} />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </TrackingProvider>
  );
};

export default AssignedSurveyHistoryDialog;
